import { z } from 'zod';
import { FORMULA_DECIMALS_MAX, FORMULA_DECIMALS_MIN, FORMULA_MAX_LENGTH, FormulaError, collectFormulaVariables, parseFormula, } from '../utils/formula.js';
import { LOCALE_CONFIG, SUPPORTED_LOCALES } from '../constants/app.js';
import { idSchema, isoDateSchema, isoDateTimeSchema } from './common.js';
import { customFieldEntitySchema, customFieldTypeSchema, documentTypeSchema } from './enums.js';
import { supportedLocaleSchema } from './plm-material.js';
import { translationMetadataWriteSchema } from './translation.js';
import { parseWeeklySchedule } from '../utils/weekly-schedule.js';
import { WEEKLY_SCHEDULE_STORAGE_MAX_LENGTH } from './delivery-planning.js';
export const CUSTOM_FIELD_OPTION_LABEL_MAX = 250;
export const CUSTOM_FIELD_FILTER_TEXT_MAX_LENGTH = 500;
/** Entitäten, deren Felder je Belegart freigeschaltet und vererbt werden. */
export const CUSTOM_FIELD_DOCUMENT_ENTITIES = ['DOCUMENT', 'DOCUMENT_ITEM'];
/** Belegarten, für die Zusatzfelder konfiguriert werden können. */
export const CUSTOM_FIELD_DOCUMENT_TYPES = [
    'QUOTE',
    'ORDER',
    'DELIVERY_NOTE',
    'INVOICE',
    'CANCELLATION',
    'CREDIT_NOTE',
    'PURCHASE_ORDER',
];
export const customFieldDocumentTypeSchema = z.enum(CUSTOM_FIELD_DOCUMENT_TYPES);
/** Formeln werden für Kunden-Snapshots, Belegköpfe und Positionszeilen sicher ausgewertet. */
export const CUSTOM_FIELD_FORMULA_ENTITIES = ['CUSTOMER', 'DOCUMENT', 'DOCUMENT_ITEM'];
const formulaExpressionSchema = z
    .string()
    .trim()
    .min(1)
    .max(FORMULA_MAX_LENGTH)
    .superRefine((value, ctx) => {
    try {
        parseFormula(value);
    }
    catch (error) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: error instanceof FormulaError ? error.message : 'Die Formel ist ungültig.',
        });
    }
});
/** Zulässige Werte eines Zusatzfeldes; `null` löscht den Wert. */
export const customFieldValueSchema = z.union([
    z.string(),
    z.number(),
    z.boolean(),
    z.array(z.string()),
    z.null(),
]);
export const customFieldValuesSchema = z.record(customFieldValueSchema);
const customFieldFilterKeySchema = z
    .string()
    .min(2)
    .max(50)
    .regex(/^[a-z][a-zA-Z0-9]*$/);
const customFieldFilterValueSchema = z.union([
    z.string().max(WEEKLY_SCHEDULE_STORAGE_MAX_LENGTH),
    z.number().finite(),
    z.boolean(),
    z.array(z.string().min(1).max(60)).max(50),
]);
/** Kompakte, URL-taugliche Filterwerte für freigegebene Zusatzfelder. */
export const customFieldFilterValuesSchema = z
    .record(customFieldFilterKeySchema, customFieldFilterValueSchema)
    .refine((values) => Object.keys(values).length <= 20, {
    message: 'Pro Listenabfrage sind höchstens 20 Zusatzfeldfilter zulässig.',
});
export const customFieldFiltersQuerySchema = z.preprocess((value) => {
    if (value === undefined)
        return {};
    if (typeof value !== 'string')
        return value;
    try {
        return JSON.parse(value);
    }
    catch {
        return value;
    }
}, customFieldFilterValuesSchema);
export const CUSTOM_FIELD_PARTNER_ROLES = ['CUSTOMER', 'PROSPECT', 'SUPPLIER'];
export const CUSTOM_FIELD_CONFIDENTIALITY_LEVELS = ['PUBLIC', 'INTERNAL', 'CONFIDENTIAL'];
export const CUSTOM_FIELD_GROUP_LAYOUT_WIDTHS = ['FULL', 'HALF'];
export const CUSTOM_FIELD_GROUP_FIELD_LAYOUTS = ['ONE_COLUMN', 'TWO_COLUMNS'];
export const customFieldPartnerRoleSchema = z.enum(CUSTOM_FIELD_PARTNER_ROLES);
export const customFieldConfidentialitySchema = z.enum(CUSTOM_FIELD_CONFIDENTIALITY_LEVELS);
export const customFieldGroupLayoutWidthSchema = z.enum(CUSTOM_FIELD_GROUP_LAYOUT_WIDTHS);
export const customFieldGroupFieldLayoutSchema = z.enum(CUSTOM_FIELD_GROUP_FIELD_LAYOUTS);
export const CUSTOM_FIELD_DEFINITION_STATUSES = [
    'DRAFT',
    'IN_REVIEW',
    'ACTIVE',
    'ARCHIVED',
];
export const customFieldDefinitionStatusSchema = z.enum(CUSTOM_FIELD_DEFINITION_STATUSES);
export const CUSTOM_FIELD_VERSION_STATUSES = [
    'DRAFT',
    'IN_REVIEW',
    'PUBLISHED',
    'SUPERSEDED',
];
export const customFieldVersionStatusSchema = z.enum(CUSTOM_FIELD_VERSION_STATUSES);
const customFieldPermissionSchema = z
    .string()
    .trim()
    .min(3)
    .max(100)
    .regex(/^[a-z][a-z0-9_]*:[a-z][a-z0-9_]*$/);
export const customFieldTranslationSchema = z
    .object({
    locale: supportedLocaleSchema,
    label: z.string().trim().max(120),
    description: z.string().trim().max(300).nullish(),
    helpText: z.string().trim().max(500).nullish(),
})
    .merge(translationMetadataWriteSchema);
export const customFieldGroupTranslationSchema = z
    .object({
    locale: supportedLocaleSchema,
    name: z.string().trim().max(120),
    description: z.string().trim().max(500).nullish(),
})
    .merge(translationMetadataWriteSchema);
export const customFieldMultilingualFieldSchema = z.enum([
    'label',
    'description',
    'helpText',
    'name',
    'optionLabel',
]);
export const customFieldMultilingualIssueSchema = z.object({
    path: z.string().min(1),
    locale: supportedLocaleSchema,
    field: customFieldMultilingualFieldSchema,
    optionIndex: z.number().int().min(0).optional(),
    optionId: idSchema.optional(),
    optionValue: z.string().optional(),
    reason: z.enum(['REQUIRED', 'DUPLICATE']),
});
function populated(value) {
    return typeof value === 'string' && value.trim().length > 0;
}
function localeEntries(translations) {
    return new Map(translations.map((translation) => [translation.locale, translation]));
}
function duplicateLocaleIssues(translations, prefix) {
    const seen = new Set();
    const issues = [];
    for (const translation of translations) {
        const locale = translation.locale;
        if (seen.has(locale)) {
            issues.push({
                path: `${prefix}.${locale}.locale`,
                locale,
                field: prefix === 'translations' ? 'label' : 'optionLabel',
                reason: 'DUPLICATE',
            });
        }
        seen.add(locale);
    }
    return issues;
}
/**
 * Produces locale-addressable paths independently of mounted language tabs.
 * Both API and web use this function before an activation-capable write.
 */
export function customFieldDefinitionMultilingualIssues(input) {
    const issues = duplicateLocaleIssues(input.translations, 'translations');
    const translations = localeEntries(input.translations);
    const descriptionRequired = input.translations.some((entry) => populated(entry.description));
    const helpTextRequired = input.translations.some((entry) => populated(entry.helpText));
    for (const locale of SUPPORTED_LOCALES) {
        const translation = translations.get(locale);
        if (!populated(translation?.label)) {
            issues.push({
                path: `translations.${locale}.label`,
                locale,
                field: 'label',
                reason: 'REQUIRED',
            });
        }
        if (descriptionRequired && !populated(translation?.description)) {
            issues.push({
                path: `translations.${locale}.description`,
                locale,
                field: 'description',
                reason: 'REQUIRED',
            });
        }
        if (helpTextRequired && !populated(translation?.helpText)) {
            issues.push({
                path: `translations.${locale}.helpText`,
                locale,
                field: 'helpText',
                reason: 'REQUIRED',
            });
        }
    }
    for (const [optionIndex, option] of input.options.entries()) {
        if (!option.isActive)
            continue;
        issues.push(...duplicateLocaleIssues(option.translations, `options.${String(optionIndex)}.translations`).map((issue) => ({
            ...issue,
            optionIndex,
            ...(option.id === undefined ? {} : { optionId: option.id }),
            optionValue: option.value,
        })));
        const optionTranslations = localeEntries(option.translations);
        for (const locale of SUPPORTED_LOCALES) {
            if (populated(optionTranslations.get(locale)?.label))
                continue;
            issues.push({
                path: `options.${String(optionIndex)}.translations.${locale}.label`,
                locale,
                field: 'optionLabel',
                optionIndex,
                ...(option.id === undefined ? {} : { optionId: option.id }),
                optionValue: option.value,
                reason: 'REQUIRED',
            });
        }
    }
    for (const locale of SUPPORTED_LOCALES) {
        const labels = new Map();
        for (const [optionIndex, option] of input.options.entries()) {
            if (!option.isActive)
                continue;
            const label = localeEntries(option.translations).get(locale)?.label.trim() ?? '';
            if (label === '')
                continue;
            const normalized = label.toLocaleLowerCase(LOCALE_CONFIG[locale].intlLocale);
            const firstIndex = labels.get(normalized);
            if (firstIndex === undefined) {
                labels.set(normalized, optionIndex);
                continue;
            }
            issues.push({
                path: `options.${String(optionIndex)}.translations.${locale}.label`,
                locale,
                field: 'optionLabel',
                optionIndex,
                ...(option.id === undefined ? {} : { optionId: option.id }),
                optionValue: option.value,
                reason: 'DUPLICATE',
            });
        }
    }
    return issues;
}
/** Completeness rules for category translations mirror definition descriptions. */
export function customFieldGroupMultilingualIssues(input) {
    const issues = duplicateLocaleIssues(input.translations, 'translations').map((issue) => ({ ...issue, field: 'name' }));
    const translations = localeEntries(input.translations);
    const descriptionRequired = input.translations.some((entry) => populated(entry.description));
    for (const locale of SUPPORTED_LOCALES) {
        const translation = translations.get(locale);
        if (!populated(translation?.name)) {
            issues.push({
                path: `translations.${locale}.name`,
                locale,
                field: 'name',
                reason: 'REQUIRED',
            });
        }
        if (descriptionRequired && !populated(translation?.description)) {
            issues.push({
                path: `translations.${locale}.description`,
                locale,
                field: 'description',
                reason: 'REQUIRED',
            });
        }
    }
    return issues;
}
function hasUniqueLocales(translations, ctx) {
    const seen = new Set();
    for (const [index, translation] of translations.entries()) {
        if (seen.has(translation.locale)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: [index, 'locale'],
                message: `Die Sprache "${translation.locale}" ist doppelt vorhanden.`,
            });
        }
        seen.add(translation.locale);
    }
}
const customFieldTranslationsSchema = z
    .array(customFieldTranslationSchema)
    .max(SUPPORTED_LOCALES.length)
    .superRefine(hasUniqueLocales);
const customFieldGroupTranslationsSchema = z
    .array(customFieldGroupTranslationSchema)
    .max(SUPPORTED_LOCALES.length)
    .superRefine(hasUniqueLocales);
export const customFieldOptionTranslationSchema = z
    .object({
    locale: supportedLocaleSchema,
    label: z.string().trim().max(CUSTOM_FIELD_OPTION_LABEL_MAX),
    description: z.string().trim().max(300).nullish(),
})
    .merge(translationMetadataWriteSchema);
const customFieldOptionTranslationsSchema = z
    .array(customFieldOptionTranslationSchema)
    .max(SUPPORTED_LOCALES.length)
    .superRefine(hasUniqueLocales);
export const customFieldOptionSchema = z.object({
    id: idSchema.optional(),
    value: z
        .string()
        .trim()
        .min(1)
        .max(60)
        .regex(/^[A-Za-z0-9_-]+$/, 'Erlaubt sind Buchstaben, Ziffern, Bindestrich und Unterstrich.'),
    label: z.string().trim().max(CUSTOM_FIELD_OPTION_LABEL_MAX),
    sortOrder: z.number().int().min(0).max(999).default(0),
    translations: customFieldOptionTranslationsSchema.default([]),
    isActive: z.boolean().default(true),
});
function zodPath(path) {
    return path.split('.').map((part) => (/^\d+$/u.test(part) ? Number(part) : part));
}
function addMultilingualIssues(ctx, issues) {
    for (const issue of issues) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: zodPath(issue.path),
            message: issue.reason === 'DUPLICATE'
                ? 'Diese Übersetzung ist innerhalb der Sprache doppelt vorhanden.'
                : 'Diese Übersetzung fehlt oder besteht nur aus Leerzeichen.',
        });
    }
}
export const customFieldValidationSchema = z.object({
    minLength: z.number().int().min(0).max(10_000).optional(),
    maxLength: z.number().int().min(1).max(10_000).optional(),
    pattern: z.string().max(200).optional(),
    min: z.number().optional(),
    max: z.number().optional(),
});
export const customFieldGroupSummarySchema = z.object({
    id: idSchema,
    key: z.string(),
    name: z.string(),
    description: z.string().nullable(),
    icon: z.string().nullable(),
    color: z.string().nullable(),
    sortOrder: z.number().int(),
    isActive: z.boolean(),
    defaultExpanded: z.boolean(),
    layoutWidth: customFieldGroupLayoutWidthSchema,
    fieldLayout: customFieldGroupFieldLayoutSchema,
    hideEmptyInDetail: z.boolean(),
    entityTypes: z.array(customFieldEntitySchema),
    partnerRoles: z.array(customFieldPartnerRoleSchema),
    readPermissions: z.array(customFieldPermissionSchema),
    writePermissions: z.array(customFieldPermissionSchema),
    isSystem: z.boolean(),
    archivedFieldCount: z.number().int().min(0).nullable(),
    archivedValueCount: z.number().int().min(0).nullable(),
    translations: customFieldGroupTranslationsSchema,
    archivedAt: isoDateTimeSchema.nullable(),
    createdAt: isoDateTimeSchema,
    updatedAt: isoDateTimeSchema,
});
export const customFieldGroupSchema = customFieldGroupSummarySchema.extend({
    fieldCount: z.number().int().min(0).default(0),
    valueCount: z.number().int().min(0).default(0),
});
export const customFieldGroupWriteSchema = z
    .object({
    key: z
        .string()
        .trim()
        .min(2)
        .max(60)
        .regex(/^[a-z][a-z0-9-]*$/, 'Der Schlüssel beginnt mit einem Kleinbuchstaben und enthält nur Kleinbuchstaben, Ziffern und Bindestriche.'),
    translations: customFieldGroupTranslationsSchema,
    icon: z.string().trim().max(60).nullish(),
    color: z
        .string()
        .trim()
        .regex(/^#[0-9A-Fa-f]{6}$/, 'Die Farbe muss als sechsstelliger Hex-Wert angegeben werden.')
        .nullish(),
    sortOrder: z.number().int().min(0).max(999).default(0),
    isActive: z.boolean().default(true),
    defaultExpanded: z.boolean().default(true),
    layoutWidth: customFieldGroupLayoutWidthSchema.default('FULL'),
    fieldLayout: customFieldGroupFieldLayoutSchema.default('TWO_COLUMNS'),
    hideEmptyInDetail: z.boolean().default(true),
    entityTypes: z.array(customFieldEntitySchema).min(1).max(20),
    partnerRoles: z.array(customFieldPartnerRoleSchema).max(3).default([]),
    readPermissions: z.array(customFieldPermissionSchema).max(50).default([]),
    writePermissions: z.array(customFieldPermissionSchema).max(50).default([]),
})
    .superRefine((value, ctx) => {
    addMultilingualIssues(ctx, customFieldGroupMultilingualIssues(value));
    value.translations.forEach((translation, index) => {
        if (translation.origin !== undefined &&
            translation.origin !== 'MANUAL' &&
            translation.reviewStatus !== 'REVIEWED') {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['translations', index, 'reviewStatus'],
                message: 'Maschinelle Übersetzungen müssen vor der Aktivierung geprüft werden.',
            });
        }
    });
});
export const customFieldGroupUpdateSchema = customFieldGroupWriteSchema
    .innerType()
    .omit({ key: true })
    .partial()
    .superRefine((value, ctx) => {
    if (value.translations !== undefined) {
        addMultilingualIssues(ctx, customFieldGroupMultilingualIssues({ translations: value.translations }));
    }
});
export const customFieldGroupListQuerySchema = z.object({
    entityType: customFieldEntitySchema.optional(),
    partnerRoles: z
        .union([z.string(), z.array(z.string())])
        .optional()
        .transform((value) => {
        if (value === undefined)
            return [];
        const entries = Array.isArray(value) ? value : value.split(',');
        return entries.map((entry) => customFieldPartnerRoleSchema.parse(entry));
    }),
    includeArchived: z
        .union([z.boolean(), z.enum(['true', 'false'])])
        .default(false)
        .transform((value) => value === true || value === 'true'),
    locale: supportedLocaleSchema.optional(),
});
export const customFieldGroupListResponseSchema = z.object({
    data: z.array(customFieldGroupSchema),
});
export const customFieldGroupReorderSchema = z.object({
    order: z.array(idSchema).min(1).max(200),
});
/** Persistierter Auf-/Zuklappzustand einer Kategorie für den angemeldeten Benutzer. */
export const customFieldGroupStateSchema = z.object({
    groupId: idSchema,
    isExpanded: z.boolean(),
    updatedAt: isoDateTimeSchema,
});
export const customFieldGroupStateListResponseSchema = z.object({
    data: z.array(customFieldGroupStateSchema),
});
export const customFieldGroupStateWriteSchema = z.object({
    isExpanded: z.boolean(),
});
export const customFieldDefinitionUsageSchema = z.object({
    valueCount: z.number().int().min(0),
    configurationLocked: z.boolean(),
    byEntity: z.array(z.object({
        entityType: customFieldEntitySchema,
        valueCount: z.number().int().min(0),
    })),
});
export const customFieldDefinitionSchema = z.object({
    id: idSchema,
    entityType: customFieldEntitySchema,
    key: z.string(),
    label: z.string(),
    labelEn: z.string().nullable(),
    description: z.string().nullable(),
    fieldType: customFieldTypeSchema,
    isRequired: z.boolean(),
    defaultValue: customFieldValueSchema.optional(),
    options: z.array(customFieldOptionSchema),
    validation: customFieldValidationSchema,
    groupId: idSchema.nullable(),
    group: z.string().nullable(),
    fieldGroup: customFieldGroupSummarySchema.nullable(),
    entityTypes: z.array(customFieldEntitySchema),
    partnerRoles: z.array(customFieldPartnerRoleSchema),
    confidentiality: customFieldConfidentialitySchema,
    readPermissions: z.array(customFieldPermissionSchema),
    writePermissions: z.array(customFieldPermissionSchema),
    translations: customFieldTranslationsSchema,
    sortOrder: z.number().int(),
    showInList: z.boolean(),
    isFilterable: z.boolean(),
    marketingSegmentationAllowed: z.boolean(),
    showInPdf: z.boolean(),
    /** Leere Liste bedeutet: für alle Belegarten gültig. */
    applicableDocumentTypes: z.array(customFieldDocumentTypeSchema),
    inheritAcrossWorkflow: z.boolean(),
    showInItemTable: z.boolean(),
    copyToDocumentItemOnSelect: z.boolean(),
    formulaExpression: z.string().nullable(),
    formulaUnit: z.string().nullable(),
    formulaDecimals: z.number().int(),
    status: customFieldDefinitionStatusSchema,
    hasDraft: z.boolean(),
    draftUpdatedAt: isoDateTimeSchema.nullable(),
    publishedRevision: z.number().int().positive().nullable().optional(),
    draftRevision: z.number().int().positive().nullable().optional(),
    draftReviewStatus: customFieldVersionStatusSchema.nullable().optional(),
    isActive: z.boolean(),
    archivedValueCount: z.number().int().min(0).nullable(),
    usage: customFieldDefinitionUsageSchema.optional(),
    archivedAt: isoDateTimeSchema.nullable(),
    createdAt: isoDateTimeSchema,
    updatedAt: isoDateTimeSchema,
});
export const customFieldDefinitionWriteSchema = z
    .object({
    entityType: customFieldEntitySchema.default('CUSTOMER'),
    key: z
        .string()
        .trim()
        .min(2)
        .max(50)
        .regex(/^[a-z][a-zA-Z0-9]*$/, 'Der Schlüssel beginnt mit einem Kleinbuchstaben und enthält nur Buchstaben und Ziffern.'),
    label: z.string().trim().max(120),
    labelEn: z.string().trim().max(120).nullish(),
    description: z.string().trim().max(300).nullish(),
    translations: customFieldTranslationsSchema.default([]),
    fieldType: customFieldTypeSchema,
    isRequired: z.boolean().default(false),
    defaultValue: customFieldValueSchema.optional(),
    options: z.array(customFieldOptionSchema).max(100).default([]),
    validation: customFieldValidationSchema.default({}),
    groupId: idSchema.nullish(),
    group: z.string().trim().max(60).nullish(),
    entityTypes: z.array(customFieldEntitySchema).min(1).max(20).optional(),
    partnerRoles: z.array(customFieldPartnerRoleSchema).max(3).default([]),
    confidentiality: customFieldConfidentialitySchema.default('PUBLIC'),
    readPermissions: z.array(customFieldPermissionSchema).max(50).default([]),
    writePermissions: z.array(customFieldPermissionSchema).max(50).default([]),
    sortOrder: z.number().int().min(0).max(999).default(0),
    showInList: z.boolean().default(false),
    isFilterable: z.boolean().default(false),
    marketingSegmentationAllowed: z.boolean().default(false),
    showInPdf: z.boolean().default(false),
    applicableDocumentTypes: z.array(customFieldDocumentTypeSchema).max(20).default([]),
    inheritAcrossWorkflow: z.boolean().default(true),
    showInItemTable: z.boolean().default(false),
    copyToDocumentItemOnSelect: z.boolean().default(true),
    formulaExpression: formulaExpressionSchema.nullish(),
    formulaUnit: z.string().trim().max(20).nullish(),
    formulaDecimals: z
        .number()
        .int()
        .min(FORMULA_DECIMALS_MIN)
        .max(FORMULA_DECIMALS_MAX)
        .default(2),
    status: customFieldDefinitionStatusSchema.default('ACTIVE'),
})
    .superRefine((value, ctx) => {
    if (value.showInPdf && value.confidentiality !== 'PUBLIC') {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['showInPdf'],
            message: 'Nur öffentliche Zusatzfelder dürfen für die PDF-Ausgabe freigegeben werden.',
        });
    }
    if (value.status === 'ACTIVE') {
        addMultilingualIssues(ctx, customFieldDefinitionMultilingualIssues({
            translations: value.translations,
            options: value.options,
        }));
        value.translations.forEach((translation, index) => {
            if (translation.origin !== undefined &&
                translation.origin !== 'MANUAL' &&
                translation.reviewStatus !== 'REVIEWED') {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['translations', index, 'reviewStatus'],
                    message: 'Maschinelle Übersetzungen müssen vor der Aktivierung geprüft werden.',
                });
            }
        });
        value.options.forEach((option, optionIndex) => option.translations.forEach((translation, translationIndex) => {
            if (translation.origin !== undefined &&
                translation.origin !== 'MANUAL' &&
                translation.reviewStatus !== 'REVIEWED') {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['options', optionIndex, 'translations', translationIndex, 'reviewStatus'],
                    message: 'Maschinelle Übersetzungen müssen vor der Aktivierung geprüft werden.',
                });
            }
        }));
    }
    const entityTypes = value.entityTypes ?? [value.entityType];
    if (!entityTypes.includes(value.entityType)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['entityTypes'],
            message: 'Der primäre Datenbereich muss in der Entitätsgültigkeit enthalten sein.',
        });
    }
    const needsOptions = value.fieldType === 'SELECT' || value.fieldType === 'MULTI_SELECT';
    if (value.fieldType === 'WEEKLY_SCHEDULE' &&
        value.defaultValue !== undefined &&
        value.defaultValue !== null &&
        value.defaultValue !== '' &&
        parseWeeklySchedule(value.defaultValue) === null) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['defaultValue'],
            message: 'customFields.weeklySchedule.invalid',
        });
    }
    if (needsOptions && value.options.length === 0) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['options'],
            message: 'Auswahlfelder benötigen mindestens eine Option.',
        });
    }
    if (!needsOptions && value.options.length > 0) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['options'],
            message: 'Optionen sind nur für Auswahlfelder zulässig.',
        });
    }
    const duplicate = value.options.find((option, index) => value.options.findIndex((other) => other.value === option.value) !== index);
    if (duplicate !== undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['options'],
            message: `Der Optionswert "${duplicate.value}" ist doppelt vergeben.`,
        });
    }
    const isDocumentScoped = (value.entityTypes ?? [value.entityType]).some((entityType) => ['CUSTOMER', ...CUSTOM_FIELD_DOCUMENT_ENTITIES].includes(entityType));
    if (!isDocumentScoped && value.applicableDocumentTypes.length > 0) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['applicableDocumentTypes'],
            message: 'Belegarten lassen sich nur für Kunden-, Belegkopf- und Positionsdaten einschränken.',
        });
    }
    if (value.fieldType === 'FORMULA') {
        if (!CUSTOM_FIELD_FORMULA_ENTITIES.includes(value.entityType)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['fieldType'],
                message: 'Berechnete Felder sind nur für Kunden, Belegköpfe und Beleg-Positionszeilen verfügbar.',
            });
        }
        if (value.formulaExpression === undefined || value.formulaExpression === null) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['formulaExpression'],
                message: 'Ein berechnetes Feld benötigt eine Formel.',
            });
        }
        else if (value.entityType === 'DOCUMENT') {
            try {
                const variables = collectFormulaVariables(parseFormula(value.formulaExpression));
                if (variables.some((variable) => !variable.startsWith('document.customFields.'))) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        path: ['formulaExpression'],
                        message: 'Belegkopf-Formeln dürfen nur document.customFields.* verwenden.',
                    });
                }
            }
            catch {
                // Die spezifische Syntaxmeldung stammt bereits aus formulaExpressionSchema.
            }
        }
        if (value.isRequired) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['isRequired'],
                message: 'Berechnete Felder werden nicht erfasst und können keine Pflichtfelder sein.',
            });
        }
    }
    else if (value.formulaExpression !== undefined && value.formulaExpression !== null) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['formulaExpression'],
            message: 'Eine Formel ist nur für berechnete Felder zulässig.',
        });
    }
});
/** Der Schlüssel und der Feldtyp sind nach dem Anlegen fix, weil Werte daran hängen. */
export const customFieldDefinitionUpdateSchema = customFieldDefinitionWriteSchema
    .innerType()
    .omit({ entityType: true, key: true, fieldType: true })
    .partial()
    .strict()
    .superRefine((value, ctx) => {
    if (value.status === 'ACTIVE' && value.translations !== undefined) {
        addMultilingualIssues(ctx, customFieldDefinitionMultilingualIssues({
            translations: value.translations,
            options: value.options ?? [],
        }));
        value.translations.forEach((translation, index) => {
            if (translation.origin !== undefined &&
                translation.origin !== 'MANUAL' &&
                translation.reviewStatus !== 'REVIEWED') {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['translations', index, 'reviewStatus'],
                    message: 'Maschinelle Übersetzungen müssen vor der Aktivierung geprüft werden.',
                });
            }
        });
        value.options?.forEach((option, optionIndex) => option.translations.forEach((translation, translationIndex) => {
            if (translation.origin !== undefined &&
                translation.origin !== 'MANUAL' &&
                translation.reviewStatus !== 'REVIEWED') {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['options', optionIndex, 'translations', translationIndex, 'reviewStatus'],
                    message: 'Maschinelle Übersetzungen müssen vor der Aktivierung geprüft werden.',
                });
            }
        }));
    }
    else if (value.status === 'ACTIVE' && value.options !== undefined) {
        const optionIssues = customFieldDefinitionMultilingualIssues({
            translations: SUPPORTED_LOCALES.map((locale) => ({ locale, label: locale })),
            options: value.options,
        }).filter((issue) => issue.field === 'optionLabel');
        addMultilingualIssues(ctx, optionIssues);
    }
});
export const CUSTOM_FIELD_IMPACT_USAGE_KINDS = [
    'FORMS',
    'LISTS',
    'FILTERS',
    'FORMULAS',
    'PDF_TEMPLATES',
    'SPECIFICATIONS',
    'IMPORT_PROFILES',
    'DOCUMENTS',
    'AUTOMATIONS',
];
export const CUSTOM_FIELD_IMPACT_CHANGE_KINDS = [
    'KEY',
    'FIELD_TYPE',
    'OPTIONS',
    'FORMULA',
    'SCOPE',
    'REQUIRED',
    'DEFAULT',
    'VISIBILITY',
    'TRANSLATIONS',
];
export const customFieldImpactRequestSchema = z
    .object({ candidate: customFieldDefinitionWriteSchema.innerType().partial().strict().optional() })
    .strict();
export const customFieldImpactReferenceSchema = z.object({
    id: idSchema,
    label: z.string().min(1).max(240),
});
export const customFieldImpactUsageSchema = z.object({
    kind: z.enum(CUSTOM_FIELD_IMPACT_USAGE_KINDS),
    count: z.number().int().min(0),
    references: z.array(customFieldImpactReferenceSchema).max(100),
});
export const customFieldImpactChangeSchema = z.object({
    kind: z.enum(CUSTOM_FIELD_IMPACT_CHANGE_KINDS),
    risk: z.enum(['LOW', 'MEDIUM', 'HIGH']),
    migrationRequired: z.boolean(),
});
export const customFieldImpactSchema = z.object({
    definitionId: idSchema,
    risk: z.enum(['LOW', 'MEDIUM', 'HIGH']),
    affectedRecords: z.number().int().min(0),
    entityTypes: z.array(customFieldEntitySchema),
    partnerRoles: z.array(customFieldPartnerRoleSchema),
    usages: z.array(customFieldImpactUsageSchema).length(CUSTOM_FIELD_IMPACT_USAGE_KINDS.length),
    changes: z.array(customFieldImpactChangeSchema),
    migration: z.object({
        required: z.boolean(),
        strategy: z.enum(['NONE', 'CREATE_REPLACEMENT_AND_MIGRATE']),
        preservesHistoricalValues: z.literal(true),
        steps: z.array(z.enum([
            'CREATE_REPLACEMENT',
            'MAP_VALUES',
            'VERIFY_VALUES',
            'PUBLISH_REPLACEMENT',
            'ARCHIVE_ORIGINAL',
        ])),
    }),
});
export const customFieldLifecycleInputSchema = z
    .object({ reason: z.string().trim().min(3).max(500) })
    .strict();
export const customFieldPublishingSettingsSchema = z
    .object({ fourEyesEnabled: z.boolean() })
    .strict();
export const customFieldDefinitionVersionSchema = z.object({
    id: idSchema,
    definitionId: idSchema,
    revision: z.number().int().positive(),
    status: customFieldVersionStatusSchema,
    configuration: customFieldDefinitionWriteSchema,
    reason: z.string().nullable(),
    createdByUserId: idSchema,
    submittedAt: isoDateTimeSchema.nullable(),
    submittedByUserId: idSchema.nullable(),
    publishedAt: isoDateTimeSchema.nullable(),
    publishedByUserId: idSchema.nullable(),
    createdAt: isoDateTimeSchema,
    updatedAt: isoDateTimeSchema,
});
export const customFieldDefinitionVersionListResponseSchema = z.object({
    data: z.array(customFieldDefinitionVersionSchema),
});
export const CUSTOM_FIELD_TRANSFER_FORMATS = ['csv', 'xlsx'];
export const CUSTOM_FIELD_IMPORT_ROW_STATUSES = ['NEW', 'CHANGED', 'UNCHANGED', 'INVALID'];
export const customFieldDefinitionExportQuerySchema = z
    .object({ format: z.enum(CUSTOM_FIELD_TRANSFER_FORMATS).default('xlsx') })
    .strict();
export const customFieldDefinitionImportErrorSchema = z.object({
    rowNumber: z.number().int().positive(),
    column: z.string().min(1).max(120),
    path: z.string().min(1).max(300),
    message: z.string().min(1).max(500),
});
export const customFieldDefinitionImportRowSchema = z.object({
    rowNumber: z.number().int().positive(),
    key: z.string().max(50),
    entityType: customFieldEntitySchema.nullable(),
    label: z.string().max(120),
    status: z.enum(CUSTOM_FIELD_IMPORT_ROW_STATUSES),
    definitionId: idSchema.nullable(),
    errors: z.array(customFieldDefinitionImportErrorSchema),
});
export const customFieldDefinitionImportCountsSchema = z.object({
    total: z.number().int().min(0),
    new: z.number().int().min(0),
    changed: z.number().int().min(0),
    unchanged: z.number().int().min(0),
    invalid: z.number().int().min(0),
});
export const customFieldDefinitionImportPreviewSchema = z.object({
    id: idSchema,
    fileName: z.string().min(1).max(255),
    fileFormat: z.enum(['CSV', 'XLSX']),
    checksum: z.string().regex(/^[a-f0-9]{64}$/u),
    counts: customFieldDefinitionImportCountsSchema,
    rows: z.array(customFieldDefinitionImportRowSchema),
    canExecute: z.boolean(),
    createdAt: isoDateTimeSchema,
});
export const customFieldDefinitionImportExecuteSchema = z
    .object({
    idempotencyKey: z.string().trim().min(8).max(200),
    generateMissingTranslations: z.boolean().default(false),
    sourceLocale: supportedLocaleSchema.optional(),
})
    .strict()
    .superRefine((value, ctx) => {
    if (value.generateMissingTranslations && value.sourceLocale === undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['sourceLocale'],
            message: 'Für die automatische Ergänzung ist eine Ausgangssprache erforderlich.',
        });
    }
});
export const customFieldDefinitionImportResultSchema = z.object({
    id: idSchema,
    status: z.enum(['COMPLETED', 'COMPLETED_WITH_ERRORS']),
    counts: customFieldDefinitionImportCountsSchema,
    rows: z.array(customFieldDefinitionImportRowSchema),
    translationBatchId: idSchema.nullable(),
    completedAt: isoDateTimeSchema,
});
export const CUSTOM_FIELD_QUALITY_ISSUE_KINDS = [
    'MISSING_TRANSLATION',
    'UNREVIEWED_MACHINE_TRANSLATION',
    'INVALID_FORMULA',
    'UNUSED_DEFINITION',
    'ARCHIVE_CANDIDATE',
    'DEFINITION_CONFLICT',
    'FAILED_TRANSLATION_JOB',
];
export const customFieldQualityQuerySchema = z
    .object({
    entityType: customFieldEntitySchema.optional(),
    categoryId: idSchema.optional(),
    locale: supportedLocaleSchema.optional(),
    status: customFieldDefinitionStatusSchema.optional(),
})
    .strict();
export const customFieldQualityIssueSchema = z.object({
    kind: z.enum(CUSTOM_FIELD_QUALITY_ISSUE_KINDS),
    severity: z.enum(['INFO', 'WARNING', 'ERROR']),
    resourceKind: z.enum(['DEFINITION', 'JOB']),
    resourceId: idSchema,
    definitionId: idSchema.nullable(),
    name: z.string().min(1).max(240).nullable(),
    key: z.string().max(120).nullable(),
    entityTypes: z.array(customFieldEntitySchema),
    categoryId: idSchema.nullable(),
    categoryName: z.string().max(240).nullable(),
    status: customFieldDefinitionStatusSchema.nullable(),
    locale: supportedLocaleSchema.nullable(),
    count: z.number().int().positive(),
    href: z.string().min(1).max(500),
    trackingId: z.string().min(1).max(80).nullable(),
    updatedAt: isoDateTimeSchema,
});
export const customFieldQualityResponseSchema = z.object({
    summary: z.object({
        activeDefinitions: z.number().int().min(0),
        incompleteDrafts: z.number().int().min(0),
        missingTranslations: z.number().int().min(0),
        unreviewedMachineTranslations: z.number().int().min(0),
        invalidFormulas: z.number().int().min(0),
        unusedDefinitions: z.number().int().min(0),
        archiveCandidates: z.number().int().min(0),
        definitionConflicts: z.number().int().min(0),
        failedTranslationJobs: z.number().int().min(0),
    }),
    byLocale: z.array(z.object({
        locale: supportedLocaleSchema,
        completeDefinitions: z.number().int().min(0),
        totalDefinitions: z.number().int().min(0),
        missingTranslations: z.number().int().min(0),
    })),
    issues: z.array(customFieldQualityIssueSchema),
    generatedAt: isoDateTimeSchema,
});
export const customFieldDefinitionListQuerySchema = z.object({
    entityType: customFieldEntitySchema.default('CUSTOMER'),
    partnerRoles: z
        .union([z.string(), z.array(z.string())])
        .optional()
        .transform((value) => {
        if (value === undefined)
            return [];
        const entries = Array.isArray(value) ? value : value.split(',');
        return entries.map((entry) => customFieldPartnerRoleSchema.parse(entry));
    }),
    locale: supportedLocaleSchema.optional(),
    /** Begrenzt Beleg-Zusatzfelder auf eine Belegart; Felder ohne Einschränkung bleiben enthalten. */
    documentType: documentTypeSchema.optional(),
    includeArchived: z
        .union([z.boolean(), z.enum(['true', 'false'])])
        .default(false)
        .transform((value) => value === true || value === 'true'),
    includeUsage: z
        .union([z.boolean(), z.enum(['true', 'false'])])
        .default(false)
        .transform((value) => value === true || value === 'true'),
    includeDrafts: z
        .union([z.boolean(), z.enum(['true', 'false'])])
        .default(false)
        .transform((value) => value === true || value === 'true'),
});
export const customFieldDefinitionListResponseSchema = z.object({
    data: z.array(customFieldDefinitionSchema),
});
/** Neue Reihenfolge nach dem Ziehen und Ablegen im Feld-Builder. */
export const customFieldReorderSchema = z
    .object({
    entityType: customFieldEntitySchema,
    order: z.array(idSchema).min(1).max(200).optional(),
    placements: z
        .array(z.object({
        id: idSchema,
        groupId: idSchema,
        sortOrder: z.number().int().min(0).max(999),
    }))
        .min(1)
        .max(500)
        .optional(),
})
    .refine((value) => value.order !== undefined || value.placements !== undefined, {
    message: 'Eine Reihenfolge oder Feldplatzierungen sind erforderlich.',
});
/** Ein Wert je Feldtyp, wie ihn die UI liefert (Datumsfelder als `YYYY-MM-DD`). */
export const customFieldTypedValueSchema = z.union([
    z.string(),
    z.number(),
    z.boolean(),
    z.array(z.string()),
    isoDateSchema,
    z.null(),
]);
//# sourceMappingURL=custom-field.js.map